if (!customElements.get("page-header-logo-banner")) {
  customElements.define(
    "page-header-logo-banner",
    class PageHeaderLogoBannerSection extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
        this.video = this.querySelector(".wt-video__movie video");
        this.lowBatteryClass = "low-battery-mode";
        this.isLowBattery = false;
        this.isMobile = window.matchMedia("(max-width: 899px)").matches;
        this.elementToScale = this.querySelector(".wt-logo-banner__logo");

        this.isSticky = false;
        this.observer = null;
        this.scrollListener = null;
        this.resizeListener = null;
        this.loadListener = null;
        this.scrollTicking = false;
        this.maxScale = null;
        this.readyClass = "wt-logo-banner--ready";
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        document.removeEventListener(
          "shopify:section:load",
          this.handleShopifyEditorEvent,
        );

        // Disconnect observer if it exists
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        // Remove scroll event listener if it exists
        if (this.scrollListener) {
          document.removeEventListener("scroll", this.scrollListener);
          this.scrollListener = null;
        }

        if (this.resizeListener) {
          window.removeEventListener("resize", this.resizeListener);
          this.resizeListener = null;
        }

        if (this.loadListener) {
          window.removeEventListener("load", this.loadListener);
          this.loadListener = null;
        }
      }

      handleShopifyEditorEvent() {
        this.reinit();
      }

      isValidSectionsOrder() {
        const pageHeader = document.body.querySelector("header.page-header");
        const currentSection = this.section;

        if (pageHeader && currentSection) {
          let sibling = pageHeader.nextElementSibling;
          while (sibling && sibling.tagName.toLowerCase() !== "section") {
            sibling = sibling.nextElementSibling;
          }
          return sibling === currentSection;
        }
        return false;
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--transparent";
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        this.observer.observe(this.logoBanner);
      }

      getNominalWidth() {
        const header = document.querySelector("header");
        const computedStyle = window.getComputedStyle(header);
        const nominalWidthMobile = computedStyle
          .getPropertyValue("--logo-width")
          .trim();
        const nominalWidth = computedStyle
          .getPropertyValue("--logo-width-desk")
          .trim();

        return parseInt(this.isMobile ? nominalWidthMobile : nominalWidth);
      }

      calculateMaxScale() {
        const nominalWidth = this.getNominalWidth();
        const screenWidth = window.innerWidth;
        const maxLogoWidth = screenWidth * 0.7;
        const maxScale = maxLogoWidth / nominalWidth;
        return Math.min(maxScale, 6);
      }

      // La escala máxima solo cambia con el ancho de pantalla: se guarda en lugar de
      // recalcularla (getComputedStyle) en cada evento de scroll
      getMaxScale() {
        if (!Number.isFinite(this.maxScale)) {
          this.maxScale = this.calculateMaxScale();
        }
        return this.maxScale;
      }

      setInitialScale() {
        this.maxScale = this.calculateMaxScale();
        this.elementToScale.style.transform = `scale(${this.maxScale})`;
      }

      handleLogoSize() {
        const logoBanner = this.logoBanner;
        const elementToScale = this.elementToScale;
        const logoWrapper = this.logoBanner.querySelector(
          ".wt-logo-banner__picture",
        );
        const maxScale = this.getMaxScale();

        const containerBottom = logoBanner.getBoundingClientRect().bottom;
        const visibilityRatio = containerBottom / window.innerHeight;
        const logoWrapperHeight = Math.max(0, containerBottom);
        const vanishingClass = "wt-logo-banner--vanishing";
        const inactiveClass = "inactive";

        let scale = Math.max(
          1,
          Math.min(maxScale, (maxScale * containerBottom) / window.innerHeight),
        );

        if (visibilityRatio < 0.7) {
          logoBanner.classList.add(vanishingClass);
        } else {
          logoBanner.classList.remove(vanishingClass);
        }

        logoWrapper.style.height = `${logoWrapperHeight}px`;
        elementToScale.style.transform = `scale(${scale})`;

        if (logoWrapperHeight < 1) {
          logoWrapper.classList.add(inactiveClass);
        } else {
          logoWrapper.classList.remove(inactiveClass);
        }
      }

      observeAndScaleElement() {
        this.setInitialScale();
        if (this.scrollListener) return;

        // Como máximo un cálculo por cuadro de pantalla y sin bloquear el scroll (passive)
        this.scrollListener = () => {
          if (this.scrollTicking) return;
          this.scrollTicking = true;
          window.requestAnimationFrame(() => {
            this.scrollTicking = false;
            this.handleLogoSize();
          });
        };
        document.addEventListener("scroll", this.scrollListener, {
          passive: true,
        });

        // Si cambia el tamaño de pantalla o termina de cargar el CSS, recalcular la escala
        this.resizeListener = () => {
          this.maxScale = null;
          this.scrollListener();
        };
        window.addEventListener("resize", this.resizeListener, { passive: true });
        if (document.readyState !== "complete") {
          this.loadListener = this.resizeListener;
          window.addEventListener("load", this.loadListener, { once: true });
        }
      }

      // calculateOffset() {
      //   const header = document.querySelector("header");
      //   const headerHeight = header.offsetHeight;
      //
      //   const announcement = document.querySelector(".wt-announcement");
      //   const announcementHeight = announcement ? announcement.offsetHeight : 0;
      //   const offset = this.isTransparentHeaderEnabled()
      //     ? headerHeight + announcementHeight
      //     : 0;
      //
      //   return offset;
      // }

      // setTopMargin() {
      //   const offset = this.calculateOffset();
      //   this.section.style.marginTop = `-${offset}px`;
      // }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          this.isValidSectionsOrder() &&
          header.classList.contains("wt-header--v3")
        );
      }

      setReadyClass() {
        this.classList.add(this.readyClass);
      }

      init() {
        document.addEventListener(
          "shopify:section:load",
          this.handleShopifyEditorEvent.bind(this),
        );

        this.setInitialScale();
        this.handleLogoSize();

        this.setupHeaderAndObservers();
        this.initializeVideoAndScaling();

        if (!this.video) {
          this.setReadyClass();
        }
      }

      setupHeaderMode() {
        const header = document.querySelector(".wt-header");
        const stickyHeaderThreshold = document.querySelector(
          ".sticky-header__threshold",
        );
        const isHeaderSticky =
          document.body.classList.contains("page-header-sticky");

        if (this.isTransparentHeaderEnabled()) {
          const isHeaderTransparent = header.classList.contains(
            "wt-header--transparent",
          );

          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = "110vh";
            // this.setTopMargin();
          } else if (isHeaderTransparent) {
            // this.setTopMargin();
          }
        } else if (isHeaderSticky) {
          stickyHeaderThreshold.style.height = "200px";
          this.section.style.marginTop = "0";
        }
      }

      setupHeaderAndObservers() {
        this.setupHeaderMode();

        if (this.isTransparentHeaderEnabled()) {
          this.observeHeader();
        }
      }

      initializeVideoAndScaling() {
        this.video
          ?.play()
          .catch((err) => {
            this.isLowBattery = true;
          })
          .finally(() => {
            if (this.isLowBattery) {
              this.classList.add(this.lowBatteryClass);
              // this.setInitialScale();
            } else {
              this.observeAndScaleElement();
            }

            this.handleLogoSize();
            this.setReadyClass();
          });

        const isDesignMode = this.dataset.designMode;
        if (!this.video || isDesignMode) {
          this.observeAndScaleElement();
        }

        // this.handleLogoSize();
      }

      reinit() {
        this.setupHeaderAndObservers();
        setTimeout(this.handleLogoSize.bind(this), 0);
      }
    },
  );
}
